import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { InputText } from 'primereact/inputtext'
import { t } from 'i18next'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { supabase } from 'Services/supabaseClient'
import { Button } from 'primereact/button'
import { commonMethods } from 'Utils/commonMethods'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import { DialogModal } from 'Components/UIComponents'

const CommunityManagement = ({ setLoading }) => {
  const history = useHistory()

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [communityManagementData, setCommunityManagementData] = useState([])
  const [allCommunityManagementData, setAllCommunityManagementData] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [dialogState, setDialogState] = useState({ showModal: false, modalType: '', message: '', onSuccess: () => {}, onHide: () => {}, onRetry: () => {}, onDismiss: () => handleDismiss() })

  const rowCount = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobileRowCount : 5
  const paginationButtons = windowSize < TABLE_CONST.maxWidthTab ? TABLE_CONST.mobilePaginationButtonCount : TABLE_CONST.paginationButtonCount

  useEffect(() => {
    getAllCommunities()

    const handleWindowResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const getAllCommunities = async () => {
    try {
      setLoading(true)

      // Step 1: Fetch all communities
      const { data: communities, error: communitiesError } = await supabase.from('communities').select('name, id, description, created_at, required_subscription, is_internal, default_sort_order,admin_only_message, country_specific, country_id  ').eq('is_internal', false)

      if (communitiesError) {
        setLoading(false)

        return
      }

      if (!communities || communities.length === 0) {
        setLoading(false)

        return
      }

      // Step 2: Extract community IDs
      const communityIds = communities.map((c) => c.id)

      // Step 3: Fetch all members for these communities in one call
      const { data: members, error: membersError } = await supabase.from('community_members').select('community_id').in('community_id', communityIds)

      if (membersError) {
        setLoading(false)

        return
      }

      // Step 4: Map communities and calculate member counts
      const results = communities.map((community) => {
        const memberCount = members ? members.filter((member) => member.community_id === community.id).length : 0

        return {
          name: community.name,
          id: community.id,
          description: community.description,
          descriptionField: community.description || '--',
          created_at: commonMethods.utcToLocalTime(community.created_at),
          memberCount,
          required_subscription: community.required_subscription,
          default_sort_order: community.default_sort_order,
          country_id: community.country_id,
          admin_only_message: community.admin_only_message,
          country_specific: community.country_specific
        }
      })

      setCommunityManagementData(results)
      setAllCommunityManagementData(results)
    } catch (error) {
      setDialogState({
        showModal: true,
        modalType: 'info',
        message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
        onHide: handleDismiss,
        onRetry: () => getAllCommunities()
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (e) => {
    const searchValue = e.target?.value?.toLowerCase()

    setSearchTerm(searchValue)

    if (searchValue === '') {
      setCommunityManagementData(allCommunityManagementData)
    } else {
      const res = allCommunityManagementData?.filter((item) => item?.name?.toLowerCase().includes(searchValue) || item?.description?.toLowerCase().includes(searchTerm) || item?.id?.toLowerCase().includes(searchTerm))

      setCommunityManagementData(res)
    }
  }

  const handleAddButtonClick = () => {
    history.push({
      pathname: ROUTE_STRINGS.addCommunityManagement
    })
  }

  const handleUsersTableClick = (rowData) => {
    const cleanRowData = {
      id: rowData.id,
      name: rowData.name
    }

    history.push({
      pathname: ROUTE_STRINGS.userCommunityManagement,
      state: cleanRowData
    })
  }

  const handleEditCommunity = (rowData) => {
    history.push({
      pathname: ROUTE_STRINGS.editCommunityManagement,
      state: rowData
    })
  }

  const handleDismiss = () => {
    setDialogState({ ...dialogState, showModal: false })
  }

  const handleDeleteCommunity = (rowData) => {
    setDialogState({
      ...dialogState,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YPU_SURE_WANT_TO_DELETE_THIS_COMMUNITY'),
      onConfirmation: () => DeleteCommunity(rowData),
      onHide: () => handleDismiss()
    })
  }

  const DeleteCommunity = async (rowData) => {
    try {
      setLoading(true)

      const { error: deleteError, status } = await supabase.from('communities').delete().eq('id', rowData.id).select()

      if (deleteError) {
        setDialogState({
          ...dialogState,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_DELETE_COMMUNITY'),
          onHide: handleDismiss,
          onRetry: () => DeleteCommunity(rowData)
        })

        return
      }

      if (status === 200) {
        const updatedCommunityData = communityManagementData.filter((community) => community.id !== rowData.id)
        const updatedAllCommunityData = allCommunityManagementData.filter((community) => community.id !== rowData.id)

        setCommunityManagementData(updatedCommunityData)
        setAllCommunityManagementData(updatedAllCommunityData)
        setDialogState({
          ...dialogState,
          showModal: true,
          modalType: 'success',
          message: t('COMMUNITY_DELETED_SUCCESSFULLY'),
          onSuccess: handleDismiss,
          onHide: handleDismiss
        })
      }
    } catch (error) {
      setDialogState({
        ...dialogState,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_DELETE_COMMUNITY'),
        onHide: handleDismiss,
        onRetry: () => DeleteCommunity(rowData)
      })
    } finally {
      setLoading(false)
    }
  }

  const actionTemplate = (rowData) => (
    <div className="action-container">
      <Button type="button" icon="pi pi-user-plus" className="pi-users-icon" tooltip={t('USERS')} tooltipOptions={{ position: 'top' }} onClick={() => handleUsersTableClick(rowData)} />
      <Button type="button" icon="pi pi-pencil" className="pi-edit-icon" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => handleEditCommunity(rowData)} />
      <Button type="button" icon="pi pi-trash" className="pi-delete-icon" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleDeleteCommunity(rowData)} />
    </div>
  )

  const columns = [
    { dataField: 'name', text: t('COMMUNITY_NAME'), className: 'name-column', isSortable: true },
    { dataField: 'id', text: t('COMMUNITY_ID'), className: 'id-column', isSortable: false },
    { dataField: 'created_at', text: t('DATE'), className: 'date-column', isSortable: true },
    { dataField: 'descriptionField', text: t('DESCRIPTION'), className: 'description-column' },
    { dataField: 'memberCount', text: t('MEMBERS_COUNT'), className: 'member-column', isSortable: true },
    { body: actionTemplate, text: t('ACTIONS'), className: 'edit-column' }
  ]

  return (
    <LayoutContainer title={'Community Management'} breadCrumbsTitle={'communityManagement'} showAddButton={true} handleAddButtonClick={handleAddButtonClick}>
      <div className="community-management-div">
        <div className="search-div p-input-icon-left">
          <i className="pi pi-search" data-testid="search-icon" />

          <InputText value={searchTerm} placeholder="Search" onChange={handleSearchChange} />
        </div>
        <div className="table-div">
          <DataTable value={communityManagementData} removableSort paginator={communityManagementData?.length} scrollHeight="550px" scrollable rows={rowCount} pageLinkSize={paginationButtons}>
            {columns.map((col, i) => (
              <Column key={i} field={col.dataField} sortable={col.isSortable} header={col.text} body={col.body} className={col.className} />
            ))}
          </DataTable>
        </div>
      </div>
      <DialogModal {...dialogState} />
    </LayoutContainer>
  )
}

export default Loader(CommunityManagement)
